import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { commonImages } from '@images/common-images';
import { loginImages } from '@images/login-images';
import { settingsImages } from '@images/settings-images';
import { Office, User } from '@models/user.model';
import { ApiService } from '@services/api/api.service';
import { CalendarAPIService } from '@services/api/calendarAPI.service';
import { ContractApiService } from '@services/api/contract.service';
import { LeadsService } from '@services/api/leads.service';
import { ListingApprovalService } from '@services/api/listing-approval.service';
import { ListingPropertiesService } from '@services/api/listing-properties.service';
import { SettingService } from '@services/api/setting.service';
import { SharedService } from '@services/api/shared.service';
import { TaskService } from '@services/api/task.service';
import { UserService } from '@services/api/user.service';
import { AuthService } from '@services/auth.service';
import { Crypto } from '@services/helpers/encryption-utils';
import { FormHelper } from '@services/helpers/form-helper';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { Utils } from '@services/helpers/utils';
import { LeadManagementService } from '@services/lm-service/lead-management.service';
import { ReduxService } from '@state-management/redux.service';
import { MessageService } from '@services/util/message.service';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';

export function strictEmailValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // ensures something@domain.tld

  return emailRegex.test(value) ? null : { strictEmail: true };
}

function noWhitespaceValidator(control: FormControl): ValidationErrors | null {
  const value = control.value || '';
  return value.trim().length === 0 ? { whitespace: true } : null;
}

export function socialUrlValidator(platform: 'facebook' | 'x' | 'instagram' | 'linkedin' | 'website') {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value?.toLowerCase().trim();
    if (!value) return null;

    const domainMap = {
      facebook: 'facebook.com',
      x: 'x.com',
      instagram: 'instagram.com',
      linkedin: 'linkedin.com',
      website: '.com',
    };

    const expectedDomain = domainMap[platform];

    // Validate if the input includes the correct domain
    if (value.includes(expectedDomain)) {
      return null;
    }

    return { invalidSocialUrl: `URL must include ${expectedDomain}` };
  };
}

@Component({
  selector: 'app-edit-office-staff',
  templateUrl: './edit-office-staff.component.html',
  styleUrls: ['./edit-office-staff.component.scss'],
})
export class EditOfficeStaffComponent implements OnInit {
  @Input() isHideSideNav: boolean = true;
  @Output() isUpdateRecord = new EventEmitter<boolean>();
  @Output() staffClosed = new EventEmitter<any>();
  @Input() recordIds: any;
  @Input() tabIndex: any;

  office_permission: any[] = [
    { name: 'Approval', value: 'approval' },
    { name: 'Edit Comment', value: 'edit_comment' },
    { name: 'Delete Comment', value: 'delete_comments' },
    { name: 'Resolve Comment', value: 'resolve_comments' },
    { name: 'Approval Notifications', value: 'approval_notifications' },
  ];
  selectAssign: any[] = [
    { name: 'Staff', value: 'staff' },
    { name: 'Team', value: 'team' },
    { name: 'User Role', value: 'user_role' },
  ];
  public userTestimonials = [];
  images = settingsImages;
  common_images = commonImages;
  login_images = loginImages;
  public userRole = Crypto.decrypt(this.localStorageService.GetItem('role'));
  public user_id: null;
  public sub;
  public TempOfficeStaff: any[] = [];
  public OfficeStaff: any[] = [];
  public User: User;
  public AuthUser: User;
  public UserForm: FormGroup;
  public submitted = false;
  public loading = false;
  public error: any;
  public response: any;
  public roles: any = [];
  public groups: any = [];
  public offices: Office[] = [];
  public subOffices: any[] = [];
  public selectedGroups: Array<any>;
  public selectedOffices: string[] = [];
  public url: any;
  public randomstring: string;
  public holiday_date = null;
  public option_1 = null;
  public smtp_use_by_other: boolean;
  public assign_holiday_work = null;
  passType = 'password';
  public ExtraCols = [];
  public userExtraColValues = [];
  public officeName = null;

  showHideBio: boolean = false;

  constructor(
    public lmService: LeadManagementService,
    public api: ApiService,
    public listingApprovalService: ListingApprovalService,
    public listingService: ListingPropertiesService,
    public _leadsService: LeadsService,
    private _CalendarAPIService: CalendarAPIService,
    public taskService: TaskService,
    public contractService: ContractApiService,
    private userService: UserService,
    private sharedService: SharedService,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private Auth: AuthService,
    private formHelper: FormHelper,
    private fb: FormBuilder,
    private redux: ReduxService,
    private localStorageService: LocalStorageService,
    private messageService: MessageService,
    private _settingService: SettingService,
    private remmiConfirm: RemmiConfirmService
  ) {}

  /**
   * Initializes the component.
   * Retrieves necessary data from local storage, services, and APIs.
   * Sets up form controls and event emitters.
   */
  ngOnInit() {
    this.officeName = Crypto.decrypt(this.localStorageService.GetItem('office_name'));
    this.option_1 = 'include';
    this.createForm();
    // Recompute Assign-Agents visibility whenever the selected role changes (fires on the initial patch too).
    this.UserForm.get('role_id')?.valueChanges.subscribe(() => this.checkSelectedRoleAssistantAccess());
    // Sub offices picker is scoped to whichever main office(s) are selected.
    this.UserForm.get('office')?.valueChanges.subscribe(officeIds => this.getSubOffices(officeIds));
    this.lmService.GetProjectList();

    // Ensure project names are set properly after fetching the project list
    setTimeout(() => {
      this.lmService.ProjectList = this.lmService.ProjectList.map(x => ({
        ...x,
        Project_Name: x?.Project_Name || '-',
      }));
    }, 2000);

    this.getTeams();
    this.getEntityProjects();

    // Subscribe to route parameters to fetch user-specific data
    this.sub = this.route.params.subscribe({
      next: params => {
        this.user_id = params['id'] ? params['id'] : this.recordIds;
        this.GetAssociatedProjects();
        this.GetAssociatedTeam();
        this.getPermission();
        this.getUser(this.user_id);
        this.getAbsences();
      },
    });

    // Subscribe to authentication user updates
    this.Auth.UserSub.subscribe({
      next: user => {
        this.AuthUser = user;
      },
    });

    this.getAgents();
    this.getRoles();

    // Initialize form for Team Popup
    this.addTeam = this.fb.group({
      office_id: [null, Validators.required],
      name: [null, Validators.required],
      members: [[], Validators.required],
      leader: [null, Validators.required],
      photo: [null, ''],
    });
  }

  affected_project: any[] = [];
  tab_name = 'Staff Info';
  isOpen_project: boolean = true;

  /** Holiday or sickness — the two are recorded the same way and only differ in what they are called. */
  absenceTypes = [
    { label: 'Holiday', value: 'holiday' },
    { label: 'Sick', value: 'sick' },
  ];
  absenceType = 'holiday';
  absenceCoverUserId: any = null;
  absenceNote = '';
  absences: any[] = [];
  isAbsenceSaving = false;

  /**
   * Existing absences for this staff member. Without this the tab could only ever add — an
   * administrator could not see what was already recorded, let alone correct a wrong date.
   */
  getAbsences() {
    if (!this.user_id) {
      return;
    }
    this.userService.getStaffHolidays(this.user_id).subscribe({
      next: response => {
        this.absences = response?.data ?? [];
      },
      error: () => {
        this.absences = [];
      },
    });
  }

  absenceTypeLabel(type: string): string {
    return this.absenceTypes.find(item => item.value === type)?.label ?? 'Holiday';
  }

  /**
   * The server resolves the cover and returns it on the row, so there is no per-row lookup and a
   * cover who has since left the office still shows a name rather than a dash.
   */
  coverName(absence: any): string {
    if (!absence?.cover_user_id) {
      return 'Not covered';
    }
    return absence?.cover_user?.full_name ?? '—';
  }

  /**
   * Records a holiday or a sick period for this staff member.
   *
   * `cover_user_id` is what answers the client's "if not assigned to someone else": while the
   * absence runs the lead assignment engine skips this user, and their share goes to the nominated
   * cover if one is set, or back to the normal rotation if not.
   */
  addHoliday() {
    if (!this.holiday_date) {
      this.toastr.error('Please select date');
      return false;
    }

    const range = this.holiday_date;
    const start = new Date(Utils.getDateStringFormat(range[0]));
    const end = new Date(Utils.getDateStringFormat(range[1] ? range[1] : range[0]));

    this.isAbsenceSaving = true;
    this.userService
      .addStaffHoliday({
        type: this.absenceType,
        holiday_date: [start, end],
        start_date: start,
        end_date: end,
        office_id: this.api.getOfficeId(),
        staff_id: this.user_id,
        cover_user_id: this.absenceCoverUserId,
        note: this.absenceNote,
      })
      .subscribe({
        next: _ => {
          this.isAbsenceSaving = false;
          this.toastr.success(`${this.absenceTypeLabel(this.absenceType)} added successfully`);
          this.holiday_date = null;
          this.absenceCoverUserId = null;
          this.absenceNote = '';
          this.getAbsences();
        },
        error: error => {
          this.isAbsenceSaving = false;
          this.toastr.error(error?.error?.message ? error?.error?.message : error?.message);
        },
      });
  }

  removeAbsence(absence: any) {
    this.remmiConfirm
      .delete({
        title: 'Remove Absence?',
        message: 'This absence will be permanently removed.',
        confirmLabel: 'Remove',
        subject: this.absenceTypeLabel(absence?.type),
      })
      .subscribe(isConfirmed => {
        if (!isConfirmed) return;
        this.userService.deleteStaffHoliday(absence?._id).subscribe({
          next: _ => {
            this.toastr.success('Removed successfully');
            this.getAbsences();
          },
          error: error => {
            this.toastr.error(error?.error?.message ? error?.error?.message : error?.message);
          },
        });
      });
  }

  // Getter method to access form controls from UserForm
  get f() {
    return this.UserForm.controls;
  }

  /** Only Master Admin / Operations Manager may manage Sales Assistant → agent links. */
  get isAdminUser(): boolean {
    const role = (this.userRole || '').toLowerCase();
    return role === 'master admin' || role === 'operations manager';
  }

  /** True when the role currently selected in the form grants `can_access_other_users`. */
  selectedRoleHasAssistantAccess = false;

  /** Show the Assign-Agents control only to an admin editing a user whose selected role can act as others. */
  get showAssignAgents(): boolean {
    return this.isAdminUser && this.selectedRoleHasAssistantAccess;
  }

  /** Fetch the selected role's assigned permissions and flag whether it includes `can_access_other_users`. */
  private checkSelectedRoleAssistantAccess(): void {
    const roleId = this.UserForm?.controls['role_id']?.value;
    if (!roleId || !this.api.getOfficeId()) {
      this.selectedRoleHasAssistantAccess = false;
      return;
    }
    this.api.get('/permissions/bytype/office', { type: 'role', office_id: this.api.getOfficeId(), id: roleId }).subscribe({
      next: response => {
        const perms: any[] = response?.data || [];
        this.selectedRoleHasAssistantAccess = perms.some(p => (p?.name ?? p) === 'can_access_other_users');
      },
      error: () => {
        this.selectedRoleHasAssistantAccess = false;
      },
    });
  }

  // Updates the router history to include the user's full name
  historyUser() {
    let ls = this.localStorageService.GetItem('router-history'); // Retrieves stored router history
    let parsedItems = Crypto.decrypt(ls); // Parses the retrieved JSON string
    parsedItems.forEach(item => {
      const idFromLink = item.link.split('/').pop(); // Extracts the last part of the link as the user ID
      if (idFromLink === this.user_id) {
        // Updates the history entry with the user's full name
        item.name = `${item.title} - ${this.User?.first_name} ${this.User?.last_name}`;
      }
    });
    this.localStorageService.SetItem('router-history', Crypto.encrypt(parsedItems)); // Saves the updated history back to local storage
  }

  public listing_approval_push_notification: boolean = true;
  public listing_approval_web_notification: boolean = true;
  public listing_approval_email_notification: boolean = true;
  public is_new: boolean;

  croppedImage: any = '';
  account_status = false;
  // Fetches user details based on the provided user ID.
  getUser(user_id = null) {
    if (user_id != null) {
      // API call to fetch user details including groups, offices, and testimonials
      this.userService.getUserById(user_id, '?include=groups,offices,userTestimonials').subscribe({
        next: response => {
          this.User = response.data; // Stores the user data
          this.account_status = response.data.deactive; // Assigns the user's account status
          this.croppedImage = response?.data?.face_photo ? response.data?.face_photo : null; // Assigns profile photo
          this.smtp_use_by_other = response?.data?.smtp_use_by_other ? response?.data?.smtp_use_by_other : false; // Checks SMTP usage by others

          // Assigns notification preferences, with default values if not present
          this.listing_approval_push_notification =
            response.data.hasOwnProperty('listing_approval_push_notification') === true ? response?.data?.listing_approval_push_notification : true;
          this.listing_approval_web_notification =
            response.data.hasOwnProperty('listing_approval_web_notification') === true ? response?.data?.listing_approval_web_notification : true;
          this.listing_approval_email_notification =
            response.data.hasOwnProperty('listing_approval_email_notification') === true ? response?.data?.listing_approval_email_notification : true;

          this.is_new = response.data.hasOwnProperty('is_new') === true ? response?.data?.is_new : false; // Checks if the user is new

          this.UserForm.patchValue(this.User); // Updates form with user data
          // this.cleanAndPatchMobileNumber(response);

          // sub_offices is an array of ids; tolerate populated objects from the API
          this.UserForm.controls['sub_offices'].setValue((response?.data?.sub_offices || []).map(itm => itm?._id ?? itm));
          // default_sub_office_report may come back as a populated object; tolerate that like sub_offices above
          const defaultSubOfficeReport = response?.data?.default_sub_office_report;
          this.UserForm.controls['default_sub_office_report'].setValue(defaultSubOfficeReport?._id ?? defaultSubOfficeReport ?? null);

          // Determines access level based on role and fetches corresponding data
          if (
            this.userRole == 'master admin' ||
            this.userRole == 'operations manager' ||
            this.User.role == 'Master Admin' ||
            this.userRole == 'Operations Manager'
          ) {
            this.getGroups();
            this.getOffices();
          } else {
            this.getGroupsAssign();
            this.getMyOffices();
          }

          // Disables role selection if the user is a Master Admin but not currently logged in as one
          if (this.User.role == 'Master Admin' && this.userRole !== 'master admin') {
            this.UserForm.get('role_id').disable();
          }

          // Resets form controls for banners
          this.UserForm.controls['banner'].reset();
          this.UserForm.controls['banner_2'].reset();
          this.UserForm.controls['banner_3'].reset();

          // Stores user testimonials if available
          if (response.data.userTestimonials && response.data.userTestimonials.data.length > 0) {
            this.userTestimonials = response.data.userTestimonials.data;
          }

          // Parses and stores extra user data if available
          if (response.data.extra_data) {
            this.userExtraColValues = JSON.parse(JSON.parse(response.data.extra_data));
          }

          this.url = response.data.photo_thumb; // Stores user profile thumbnail URL

          // Updates router history after a short delay
          setTimeout(() => {
            this.historyUser();
          }, 1000);
        },
      });
    }
  }

  // Selects user groups and assigns extra data if available
  selectedGroup() {
    if (this.User.groups.data !== undefined) {
      const Groups = this.User.groups.data; // Retrieves user's groups
      if (Groups[0] !== undefined) {
        this.selectedGroups = []; // Initializes selected groups array
        Groups.forEach(g => {
          if (g._id) {
            // Finds the matching group from the available groups list
            let group = this.groups.find(itm => {
              return itm._id == g._id;
            });
            if (group) {
              this.selectedGroups.push(g._id); // Adds group ID if found
            }
          } else {
            this.selectedGroups.push(g.id); // Adds group ID if it doesn't have an `_id`
          }
        });

        // Parses extra data if available
        if (Groups[0].extra_data !== undefined && Groups[0].extra_data && Groups[0].extra_data !== 'null') {
          this.ExtraCols = JSON.parse(<any>JSON.parse(Groups[0].extra_data));
        }

        // Maps extra column values based on user data
        this.ExtraCols.forEach(c => {
          let found = this.userExtraColValues.filter(v => v.label === c['label']);
          c['value'] = found.length > 0 ? found[0].value : null;
        });

        this.UserForm.controls['group'].setValue(this.selectedGroups); // Updates form with selected groups
      }
    }
  }

  // Selects user offices and assigns selected office IDs
  selectedOffice() {
    if (this.User.offices.data !== undefined) {
      const Office = this.User.offices.data; // Retrieves user's offices
      if (Office[0] !== undefined) {
        this.selectedOffices = []; // Initializes selected offices array
        Office.forEach(g => {
          if (g._id) {
            // Finds the matching office from the available offices list
            let office = this.offices.find(itm => {
              return itm._id == g._id;
            });
            if (office) {
              this.selectedOffices.push(g._id); // Adds office ID if found
            }
          } else {
            this.selectedOffices.push(g.id); // Adds office ID if it doesn't have an `_id`
          }
        });

        this.UserForm.controls['office'].setValue(this.selectedOffices); // Updates form with selected offices
      }
    }
  }

  // Fetches all roles from the API and removes 'Master Admin' for non-master users
  getRoles() {
    this.sharedService.getDropDownItems(`roles`).subscribe({
      next: data => {
        this.roles = data?.data?.sort((a, b) => a?.name.localeCompare(b?.name)); // Stores fetched roles
        this.roles.forEach((itm, index, object) => {
          if (itm._id) {
            itm.id = itm._id; // Assigns `_id` to `id` for consistency
          }

          // Removes 'Master Admin' role for users who are not authorized
          if (this.AuthUser.role !== 'Master Admin' && itm.name == 'Master Admin' && this.User.role !== 'Master Admin') {
            object.splice(index, 1); // Removes the role from the array
          }
        });
      },
    });
  }

  // Fetches user groups from the API and processes them
  getGroups() {
    this.sharedService.getDropDownItems(`groups`).subscribe({
      next: data => {
        this.groups = data.data?.sort((a, b) => a?.name.localeCompare(b?.name)); // Stores fetched groups
        this.groups.forEach(itm => {
          if (itm._id) {
            itm.id = itm._id; // Assigns `_id` to `id` for consistency
          }
        });
        this.selectedGroup(); // Calls function to update selected groups
      },
    });
  }

  getSubOffices(officeIds: string[] = []) {
    if (!officeIds?.length) {
      this.subOffices = [];
      this.UserForm.get('sub_offices')?.setValue([]);
      this.UserForm.get('default_sub_office_report')?.setValue(null);
      return;
    }
    this._settingService.getSubOfficesByOffices(officeIds).subscribe({
      next: response => {
        this.subOffices = (response?.data || []).sort((a, b) => a?.office_name?.localeCompare(b?.office_name));
        const validIds = new Set(this.subOffices.map(o => o._id));
        const current: string[] = this.UserForm.get('sub_offices')?.value || [];
        const pruned = current.filter(id => validIds.has(id));
        if (pruned.length !== current.length) {
          this.UserForm.get('sub_offices')?.setValue(pruned);
        }
        const defaultOffice = this.UserForm.get('default_sub_office_report')?.value;
        if (defaultOffice && !validIds.has(defaultOffice)) {
          this.UserForm.get('default_sub_office_report')?.setValue(null);
        }
      },
    });
  }

  // Fetches assigned groups from the API and updates the selected groups
  getGroupsAssign() {
    this.userService.getGroupsAssign().subscribe({
      next: data => {
        this.groups = data.data?.sort((a, b) => a?.name.localeCompare(b?.name)); // Stores fetched assigned groups
        // Ensures `_id` is assigned to `id` for consistency
        this.groups.forEach(itm => {
          if (itm._id) {
            itm.id = itm._id;
          }
        });
        this.selectedGroup(); // Calls function to update selected groups
      },
    });
  }

  createForm() {
    this.UserForm = new FormGroup({
      deactive: new FormControl(false),
      first_name: new FormControl('', [Validators.required, noWhitespaceValidator]),
      last_name: new FormControl('', [Validators.required, noWhitespaceValidator]),
      email: new FormControl('', [Validators.required, strictEmailValidator]),
      marketing_email: new FormControl('', [strictEmailValidator]),
      photo: new FormControl(),
      photoHR: new FormControl(),
      photoHRURL: new FormControl(),
      title: new FormControl(),
      testimonial: new FormControl(),
      service_provider_id: new FormControl(null),
      client: new FormControl(),
      area: new FormControl(),
      banner: new FormControl(),
      banner_2: new FormControl(),
      banner_3: new FormControl(),
      password: new FormControl(),
      user_level_id: new FormControl(),
      mobile: new FormControl(),
      telephone: new FormControl(),
      position: new FormControl(),
      show_on_website: new FormControl(true),
      bio: new FormControl(),
      role_id: new FormControl('', [Validators.required]),
      office: new FormControl('', [Validators.required]),
      sub_offices: new FormControl([]),
      default_sub_office_report: new FormControl(null),
      group: new FormControl(),
      facebook: new FormControl('', [socialUrlValidator('facebook')]),
      twitter: new FormControl('', [socialUrlValidator('x')]),
      instagram: new FormControl('', [socialUrlValidator('instagram')]),
      linkedin: new FormControl('', [socialUrlValidator('linkedin')]),
      website: new FormControl('', [socialUrlValidator('website')]),
      crm_password: new FormControl(),
      permissions: new FormControl(),
    });
  }

  // Updates user notification settings and saves them via API
  allowNotification() {
    let form_data = this.UserForm.value;

    // Appends additional user data to the form
    form_data['smtp_use_by_other'] = this.smtp_use_by_other;
    form_data['mobile'] = this.UserForm.controls['mobile'].value;
    form_data['listing_approval_push_notification'] = this.listing_approval_push_notification;
    form_data['listing_approval_web_notification'] = this.listing_approval_web_notification;
    form_data['listing_approval_email_notification'] = this.listing_approval_email_notification;

    // Sends an update request to the API
    this.userService.updateUser(this.user_id, form_data).subscribe({
      next: _ => {
        this.toastr.success('Staff has been updated');
      },
    });
  }

  // Updates user details and handles navigation
  updateUser(navigate: boolean) {
    if (this.userRole === 'sales agent') {
      return this.toastr.error('You are not allowed to update staff details');
    }
    this.submitted = true;
    this.loading = true;
    // Resets photo field if it remains unchanged
    if (this.UserForm.value['photo'] == this.User?.photo) {
      this.UserForm.patchValue({ photo: null });
    }

    // Ensures password meets minimum length validation
    if (this.UserForm.controls['password'].value !== '' || this.UserForm.controls['password'].value !== null) {
      this.UserForm.controls['password'].setValidators([Validators.minLength(12)]);
      this.UserForm.updateValueAndValidity();
    }

    // Validates form fields
    this.formHelper.validateAllFormFields(this.UserForm);
    if (this.UserForm.invalid) {
      this.loading = false;
      return;
    }

    // Prepares form data for submission
    this.UserForm.value['extra_data'] = JSON.stringify(this.ExtraCols);
    this.UserForm.value['user_testimonals_list'] = JSON.stringify(this.userTestimonials);
    let form_data = this.UserForm.value;
    delete form_data['show_on_website'];
    form_data['smtp_use_by_other'] = this.smtp_use_by_other;

    form_data['mobile'] = this.UserForm.controls['mobile'].value;
    form_data['telephone'] = form_data['telephone']?.toString();
    form_data['is_new'] = this.is_new;
    form_data['listing_approval_push_notification'] = this.listing_approval_push_notification;
    form_data['listing_approval_web_notification'] = this.listing_approval_web_notification;
    form_data['listing_approval_email_notification'] = this.listing_approval_email_notification;
    if (this.croppedImage) {
      form_data['face_photo'] = this.croppedImage.startsWith('https://remmi-xm.s3.ap-southeast-2.amazonaws.com')
        ? this.croppedImage.replace('https://remmi-xm.s3.ap-southeast-2.amazonaws.com/', '')
        : this.croppedImage;
    }
    // Sends update request to the API
    this.userService.updateUser(this.user_id, form_data).subscribe({
      next: data => {
        this.loading = false;
        this.submitted = false;
        this.response = data;
        this.toastr.success('Staff has been updated');

        // Resets user and office data in Redux store
        this.redux.setUser(null);
        this.redux.setMyOffices(null);

        // Handles post-update navigation
        if (!this.isHideSideNav && navigate) {
          this.isUpdateRecord.emit(true);
          this.removeLevelTab();
        } else {
          navigate && this.router.navigateByUrl('/setting/office-staff');
        }
      },
      error: error => {
        this.loading = false;
        this.submitted = false;
        let err = error?.message ?? error?.error?.message;
        this.error = Array.of(err);

        // Displays appropriate error messages
        if (error.message && error.status_code !== 500) {
          this.toastr.error(err);
        } else {
          this.toastr.error('Something wrong with the form. Please check and try again');
        }
      },
    });
  }

  // Removes a staff-related tab from the UI
  removeLevelTab() {
    let tab = this.lmService.tab_levels.find(el => el.name.includes('Staff'));
    if (tab) {
      let ind = this.lmService.tab_levels.indexOf(tab);
      tab['taskTabs'] = true;
      this.staffClosed.emit({ level: tab, index: ind });
    } else {
      this.staffClosed.emit(true);
    }
  }

  // Fetches office data from API and updates the UI
  getOffices() {
    this.sharedService.getDropDownItems(`offices`).subscribe({
      next: data => {
        this.offices = data.data?.sort((a, b) => a?.name.localeCompare(b?.name));

        // Ensures `_id` is assigned to `id` for consistency
        this.offices.forEach(itm => {
          if (itm._id) {
            itm.id = itm._id;
          }
        });

        this.selectedOffice(); // Updates selected offices
        this.loadUsers(); // Loads users associated with offices
      },
    });
  }

  // Fetches the offices assigned to the current user
  getMyOffices() {
    this.userService.getMyOffices().subscribe({
      next: data => {
        this.offices = data.data?.sort((a, b) => a?.name.localeCompare(b?.name));

        // Ensures `_id` is assigned to `id` for consistency
        this.offices.forEach(itm => {
          if (itm._id) {
            itm.id = itm._id;
          }
        });

        this.selectedOffice(); // Updates selected offices
      },
    });
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  delete() {
    this.url = null;
  }

  // Password
  public togglePasswordType() {
    if (this.passType === 'password') {
      this.passType = 'text';
    } else {
      this.passType = 'password';
    }
  }

  public generatePassword() {
    const upperCase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowerCase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const special = '!@#$%^&*()_+~`|}{[]:;?><,./-=';
    const allChars = upperCase + lowerCase + numbers + special;

    let password = '';
    // Ensure at least one of each type
    password += upperCase[Math.floor(Math.random() * upperCase.length)];
    password += lowerCase[Math.floor(Math.random() * lowerCase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];

    for (let i = password.length; i < 15; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    // Shuffle the password so first 4 positions are not predictable
    password = password
      .split('')
      .sort(() => 0.5 - Math.random())
      .join('');

    this.randomstring = password;
    this.UserForm.controls['password'].patchValue(this.randomstring);
  }

  // Updates the value of an extra field in the ExtraCols array
  addValue(i, e) {
    this.ExtraCols[i].value = e.target.value; // Sets the input value for the specified index
  }

  // Handles updates when an image is received (e.g., after cropping)
  onReceivedUpdateImages(event) {
    if (event) {
      this.croppedImage = event.face_photo; // Stores the received image
      this.getUser(this.user_id); // Reloads user data after the update
    }
  }

  // Cancels the form action and either updates the record or navigates to another page
  CancelForm() {
    if (this.isHideSideNav === false) {
      this.isUpdateRecord.emit(true); // Emits an update event
      this.removeLevelTab(); // Calls function to remove the related tab
    } else {
      this.router.navigateByUrl('/setting/office-staff'); // Navigates to the office staff page
    }
  }

  // Fetches staff list with pagination and filtering options
  getStaff(page = null, per_page = 20, role = '', query = '', SortBy = '', uid = this.api.getUserId(), officeid = this.api.getOfficeId()) {
    if (uid && officeid) {
      this.userService.getOfficeStaff(page, per_page, role, query, uid, officeid, SortBy).subscribe({
        next: response => {
          try {
            const { status, data } = response;
            if (status) {
              let active_staff = data?.filter(el => el?.deactive !== true); // Filters out deactivated staff
              this.TempOfficeStaff = this.OfficeStaff = active_staff?.filter(res => res?._id !== this.user_id); // Excludes current user
            }
          } catch (error) {
            this.TempOfficeStaff = this.OfficeStaff = []; // Handles errors by resetting staff lists
          }
        },
        error: _ => {
          this.TempOfficeStaff = this.OfficeStaff = []; // Handles API errors by clearing staff lists
        },
      });
    }
  }

  AddNewProject: any[] = [];
  AssociatedProjectSelection: any[] = [];
  AssociatedProjectsUsers: any[] = [];
  // Fetches the list of projects associated with the current user
  GetAssociatedProjects() {
    this.userService.getUserAssociatedProjects(this.user_id).subscribe({
      next: (response: any) => {
        const { status, data }: { status: boolean; data: any[] } = response;
        if (status && data?.length > 0) {
          // Maps project data and assigns project names to the list
          this.AssociatedProjectsUsers = data?.map((project: any) => ({
            ...project,
            Project_Name: project?.project?.Project_Name || '',
          }));
        } else {
          this.AssociatedProjectsUsers = []; // Resets the list if no projects are found
        }
      },
    });
  }

  // Displays a confirmation dialog and deletes selected users if confirmed.
  deleteProjects(project: any[]) {
    const count = project?.length || 0;
    this.remmiConfirm
      .delete({
        title: 'Remove From Project?',
        message: 'This staff member will no longer be associated with the selected projects.',
        confirmLabel: 'Remove',
        subject: count === 1 ? project[0]?.Project_Name : `${count} projects selected`,
      })
      .subscribe(isConfirmed => isConfirmed && this.AssociateUserToProject('remove', project)); // Calls method to remove user from projects
  }

  // Associates or removes a user from projects based on the specified action
  AssociateUserToProject(action: 'add' | 'remove', project: any[]) {
    let promise_list: any[] = [];
    if (action === 'add') {
      project.forEach((pro: any) => {
        // Adds user to the selected projects
        promise_list.push(
          this.userService.associateUserToProjectTwo({
            projectid: pro?._id ? pro?._id : pro,
            user_id: this.user_id,
          })
        );
      });
    } else {
      project.forEach((pro: any) => {
        // Removes user from the selected projects
        promise_list.push(
          this.userService.deleteAssociateUserToProjectTwo({
            projectid: pro?.projectid ? pro?.projectid : pro,
            user_id: this.user_id,
          })
        );
      });
    }

    if (promise_list?.length > 0) {
      forkJoin<any>(promise_list).subscribe({
        next: _ => {
          // Displays success message and refreshes the associated projects list
          this.toastr.success(`${action === 'add' ? 'Added successfully' : 'Removed successfully'}`);
          this.GetAssociatedProjects();
          this.lmService.GetProjectList();
          this.AssociatedProjectSelection = this.AddNewProject = []; // Resets selection lists
          this.messageService.sendMessage({ isClearItems: true });
        },
        error: _ => {
          this.toastr.error(`Something went wrong`); // Displays error message
        },
      });
    }
  }

  // Clears an item from a specific list based on the provided method
  clear(item, method) {
    if (method === 'team') {
      // Removes the specified item from the team list
      this.addNewTeam = this.addNewTeam.filter(x => x !== item);
    }
  }

  Teams: any = [];
  // Fetches the list of teams associated with the current office
  getTeams() {
    if (this.api.getOfficeId()) {
      let url = `/search-data?office_id=${this.api.getOfficeId()}&user_id=${this.api.getUserId()}&Keywords&page=1&limit=999&module=teams`;
      this.api.get(url).subscribe({
        next: response => {
          this.Teams = response.data; // Stores the retrieved teams data
        },
      });
    }
  }

  addNewTeam: any[] = []; // Stores newly added teams
  AssociatedTeamSelection: any[] = []; // Stores selected teams for association
  AssociatedTeamUsers: any[] = []; // Stores users associated with teams

  // Retrieves the teams associated with the current user
  GetAssociatedTeam() {
    this.userService.getUserTeamAssociation(this.user_id).subscribe({
      next: (response: any) => {
        this.AssociatedTeamUsers = []; // Clears the previous list
        this.AssociatedTeamUsers = response?.data; // Stores the newly retrieved list
      },
    });
  }

  // Displays a confirmation dialog and deletes selected users if confirmed.
  deleteTeams(team: any[]) {
    const count = team?.length || 0;
    this.remmiConfirm
      .delete({
        title: 'Remove From Team?',
        message: 'This staff member will no longer be a member of the selected teams.',
        confirmLabel: 'Remove',
        subject: count === 1 ? team[0]?.team?.name : `${count} teams selected`,
      })
      .subscribe(isConfirmed => isConfirmed && this.AssociateUserToTeam('remove', team)); // Calls method to remove user from teams
  }

  // Associates or removes a user from a team based on the specified action
  AssociateUserToTeam(action, value: any[]) {
    if (action == 'add') {
      // Adds the user to the selected teams
      this.api
        .post(`/user-team-association`, {
          team_id: value,
          user_id: this.user_id,
        })
        .subscribe({
          next: _ => {
            this.toastr.success('Added successfully'); // Displays success message
            this.addNewTeam = []; // Clears the new team selection
            this.getTeams();
            this.GetAssociatedTeam(); // Refreshes the associated team list
          },
        });
    } else {
      let team_id = [];
      value.forEach(id => {
        team_id.push(id?.team?._id); // Extracts team IDs from the selected value
      });
      // Removes the user from the selected teams
      this.api
        .delete(`/user-team-association-remove`, {
          team_id: team_id ? team_id : value,
          user_id: this.user_id,
        })
        .subscribe({
          next: _ => {
            this.toastr.success('Removed successfully'); // Displays success message
            this.AssociatedTeamSelection = []; // Clears the team selection
            this.messageService.sendMessage({ isClearItems: true });
            this.getTeams();
            this.GetAssociatedTeam(); // Refreshes the associated team list
          },
        });
    }
  }

  originalAssigns = [];
  // Fetches the list of agents (staff) associated with the current office
  getAgents() {
    let url = '/users/byoffice?office=' + this.api.getOfficeId() + '&show=staff';
    this.redux.getSetProjectInfo(true, 'byOffice', url).subscribe({
      next: response => {
        // Maps the retrieved staff data and constructs the full name
        this.originalAssigns = JSON.parse(JSON.stringify(response.data));
        this.originalAssigns = this.originalAssigns.map(i => {
          i.full_name = i.first_name + ' ' + i.last_name;
          return i;
        });
      },
    });
  }

  Enabled_MFA: any;
  getMFAEnabled() {
    // Fetch the currently enabled MFA type for the user
    this.api.get(`/user-mfa-type/${this.user_id}`).subscribe({
      next: response => {
        this.Enabled_MFA = response?.data?.authentication_type;
      },
    });
  }

  resetMFA() {
    this.remmiConfirm
      .ask({
        title: 'Reset MFA?',
        message: 'This user will need to set up multi-factor authentication again at their next login.',
        confirmLabel: 'Reset',
        tone: 'warning',
      })
      .subscribe(isConfirmed => {
        if (!isConfirmed) return;
        this.api.delete(`/delete-mfa/${this.user_id}`).subscribe({
          next: _ => {
            this.toastr.success('MFA reset successfull');
            this.getMFAEnabled();
          },
        });
      });
  }

  visible: boolean = false;
  close() {
    this.visible = false;
    this.addTeam.reset();
  }
  show() {
    this.visible = true;
  }
  permission_array: any[] = [];
  // Adds a new permission object to the permission_array with default values
  addPermission() {
    this.permission_array?.push({ type: null, included: null, permission: [] });
  }

  // Removes a specific permission from the permission_array based on the given index
  removePermission(i) {
    this.permission_array.splice(i, 1);
  }

  // Submits the listing approval permissions for the current user and office
  onSubmit() {
    let isValid = true;
    this.permission_array.forEach(item => {
      // Reset previous invalid flag
      item.invalid = false;
      if (!item.type || !item.included || item.included.length === 0) {
        item.invalid = true;
        isValid = false;
      }
    });

    if (!isValid) {
      this.toastr.error('Please select staff, team, or user role first');
      return;
    }

    // If valid, proceed with API call
    this.listingApprovalService
      .saveListingApproval({
        user_id: this.api.getUserId(),
        office_id: this.api.getOfficeId(),
        listing_approval_detail: this.permission_array,
      })
      .subscribe({
        next: _ => {
          this.toastr.success('Listing approval permission added successfully');
          this.getPermission();
        },
      });
  }

  // Updates the notify user receiver list based on the selected category (staff, user role, or team)
  onChangeNotifyUserReceiver(event) {
    let data: any;

    if (event?.name === 'staff') {
      // If the selected category is 'staff', use the original assigns (agents/staff)
      data = this.originalAssigns.map(res => ({ ...res, full_name: res.full_name }));
    } else if (event?.name === 'user_role') {
      // If the selected category is 'user_role', use roles with their names
      data = this.roles.map(res => ({ ...res, full_name: res.name }));
    } else if (event?.name === 'team') {
      // If the selected category is 'team', use team names
      data = this.Teams.map(res => ({ ...res, full_name: res.name }));
    } else {
      data = []; // Default to an empty array if no valid category is selected
    }

    return data; // Returns the processed data
  }

  /**
   * This file contains functions for fetching and managing different types of data
   * related to agents, including permissions, property listings, contracts, and leads.
   */

  // Fetches listing approval permissions for the currently logged-in user
  getPermission() {
    this.listingApprovalService.getListingApproval().subscribe({
      next: response => {
        // Assigns fetched permissions to the permission_array, ensuring it's not null
        this.permission_array = response['data'] === null ? [] : response['data']?.listing_approval_detail;
      },
    });
  }

  // Holds data rows for various modules
  rows = [];

  // Fetches property listings associated with the current agent
  agentListing() {
    this.listingService
      .getListingPropertiesByFilters(1, 9999, {
        office_id: this.api.getOfficeId(),
        agents: [this.user_id],
        gridview: true,
      })
      .subscribe({
        next: response => {
          // Checks if 'properties' module data already exists in rows; if not, it adds new data
          if (!this.rows?.find(ele => ele?.selected_module == 'properties')) {
            this.rows.push({
              selected_module: 'properties',
              isChecked: false,
              isAgent: false,
              data: response['data']?.map(ele => {
                // Maps response data to a structured format
                ele = {
                  _id: ele._id,
                  property_address: ele.property_address,
                  status: ele.property_status,
                  updated_at: ele.updated_at,
                  created_at: ele.created_at,
                  isChecked: false,
                  agent_id: null,
                };
                return ele;
              }),
            });
          }
        },
      });
  }

  // Fetches contract details associated with the current agent
  agentContract() {
    let params = {
      keyword: null,
      agent: JSON.stringify([this.user_id]),
      listing: null,
      project: null,
      status: null,
      filter: JSON.stringify([]),
      agent_name: JSON.stringify([this.User['full_name']]),
      condition_start: null,
      condition_end: null,
      satellment_start: null,
      satellment_end: null,
    };

    this.contractService.getContractList(params).subscribe({
      next: response => {
        // Checks if 'contract' module data already exists in rows; if not, it adds new data
        if (!this.rows?.find(ele => ele?.selected_module == 'contract')) {
          this.rows.push({
            selected_module: 'contract',
            isChecked: false,
            isAgent: false,
            data: response['data']
              ?.filter(x => x?.contractStatus !== 'Settled')
              ?.map(ele => {
                // Maps response data to a structured format
                ele = {
                  _id: ele._id,
                  property_address: ele.listingAddress || ele?.project_name,
                  status: ele.contractStatus || ele?.status_reason,
                  updated_at: ele.updated_at,
                  created_at: ele.created_at,
                  isChecked: false,
                  agent_id: null,
                };
                return ele;
              }),
          });
        }
      },
    });
  }

  // Fetches lead details associated with the current agent
  agentLead() {
    let payload = {
      page: 1,
      per_page: 999999,
      keyword: '',
      project: null,
      filter: JSON.stringify([]),
      listing: null,
      lead_type: null,
      status_type: null,
      lead_status: null,
      agent_responsible: JSON.stringify([this.user_id]),
      min_beds: null,
      max_beds: null,
      min_price: null,
      max_price: null,
      price_range: null,
      time_frame: null,
      is_active: true,
    };

    this._leadsService.getLeadById(payload).subscribe({
      next: response => {
        // Checks if 'lead_management' module data already exists in rows; if not, it adds new data
        if (!this.rows?.find(ele => ele?.selected_module == 'lead_management')) {
          this.rows.push({
            selected_module: 'lead_management',
            isChecked: false,
            isAgent: false,
            data: response['data']?.map(ele => {
              // Maps response data to a structured format
              ele = {
                _id: ele._id,
                full_name: ele.full_name,
                property_address: ele?.listing || ele?.project,
                status: ele.lead_status,
                updated_at: ele.updated_at,
                created_at: ele.created_at,
                isChecked: false,
                agent_id: null,
              };
              return ele;
            }),
          });
        }
      },
    });
  }

  /**
   * Fetches tasks assigned to the agent and updates the rows array.
   */
  agentTask() {
    let payload = {
      page_no: 1,
      per_page: 999999,
      keyword: '',
      agent: JSON.stringify([this.user_id]),
      status: null,
      due_date: null,
      filter: JSON.stringify([]),
      only: 'all',
      office_id: this.api.getOfficeId(),
      user_id: this.api.getUserId(),
    };
    this.taskService.getTaskList(payload).subscribe({
      next: response => {
        if (!this.rows?.find(ele => ele?.selected_module == 'tasks')) {
          this.rows.push({
            selected_module: 'tasks',
            isChecked: false,
            isAgent: false,
            data: response['data']
              ?.filter(x => x?.task_status_name !== 'Closed')
              ?.map(ele => {
                ele = {
                  _id: ele._id,
                  title: ele.title,
                  status: ele.task_status_name,
                  updated_at: ele.updated_at,
                  created_at: ele.created_at,
                  isChecked: false,
                  agent_id: null,
                };
                return ele;
              }),
          });
        }
      },
    });
  }

  /**
   * Retrieves project entity ID from the API and stores it in projectEntity.
   */
  projectEntity = null;
  getEntityProjects() {
    if (this.api.getOfficeId()) {
      this._leadsService.getEntity().subscribe({
        next: response => {
          response.data.forEach(rc => {
            if (rc.systementity == 'byd_projects') {
              this.projectEntity = rc._id;
            }
          });
        },
      });
    }
  }

  /**
   * Fetches agent's projects and updates the rows array.
   */
  agentProject() {
    let payload = {
      userid: this.user_id,
      officeid: this.api.getOfficeId(),
      withlookup: true,
    };
    this._leadsService.getAgentProject(this.projectEntity, payload).subscribe({
      next: response => {
        if (!this.rows?.find(ele => ele?.selected_module == 'projects')) {
          this.rows.push({
            selected_module: 'projects',
            isChecked: false,
            isAgent: false,
            data: response['data']
              ?.filter(rex => rex?.Project_Manager == this.User['full_name'])
              ?.map(ele => {
                ele = {
                  _id: ele._id,
                  project_name: ele.Project_Name,
                  status: ele.Project_Status,
                  created_at: ele.created_at,
                  updated_at: ele.updated_at,
                  isChecked: false,
                  agent_id: null,
                };
                return ele;
              }),
          });
        }
      },
    });
  }

  /**
   * Fetches inspection events assigned to the agent and updates the rows array.
   */
  getInspections() {
    this._CalendarAPIService.getCalendarEventsByModuleName(this.api.getOfficeId(), 'office_id').subscribe({
      next: response => {
        let res_data = response?.data?.filter(ele => ele?.agent && ele?.agent?._id === this.user_id);
        let today = moment();
        let pending_ins = res_data
          ?.filter(ins => {
            const eventDateTime = moment(ins?.event?.start?.dateTime);
            const eventEndDateTime = moment(ins?.event?.end?.dateTime);
            const isSameDate = eventDateTime.isSame(today, 'day');
            const isFuDate = eventDateTime.isAfter(today);
            const isFutureTime = isSameDate && eventEndDateTime.isAfter(today);
            return ins?.isInspection && (isFuDate || isFutureTime);
          })
          .sort((a, b) => <any>new Date(a?.event?.start?.dateTime) - <any>new Date(b?.event?.start?.dateTime));

        if (!this.rows?.find(ele => ele?.selected_module == 'ofi')) {
          this.rows.push({
            selected_module: 'ofi',
            data: pending_ins?.map(ele => {
              ele = {
                _id: ele._id,
                property_address: ele.property_address,
                date: ele.date,
                agent_id: null,
              };
              return ele;
            }),
          });
        }
      },
      error: () => {
        this.toastr.error('Something went wrong');
      },
    });
  }

  /**
   * Assigns staff to selected items in the rows array.
   */
  assignStaff(event, bind, ind) {
    this.rows[ind]?.data?.forEach(ele => {
      if (ele['isChecked'] == true) {
        ele['agent_id'] = event[bind];
      }
    });
  }

  /**
   * Toggles selection state for all items in a specific row index.
   */
  allColumn(event, i) {
    this.rows[i]?.data?.forEach(ele => {
      ele['isChecked'] = event?.checked;
    });
    this.selectColumn(i);
  }

  isContract = false;

  /**
   * Updates selection status for a specific row index.
   */
  selectColumn(i) {
    let checkTrue = this.rows[i]?.data?.filter(ele => ele['isChecked'] == true);
    checkTrue?.length > 0 ? (this.rows[i]['isAgent'] = true) : (this.rows[i]['isAgent'] = false);
    let every = this.rows[i]?.data?.every(ele => ele['isChecked'] == true);
    every == true ? (this.rows[i]['isChecked'] = true) : (this.rows[i]['isChecked'] = false);
  }

  /**
   * Assigns another agent to the selected properties and updates the user.
   */
  assignedOtherAgent() {
    let payload = {
      office_id: this.api.getOfficeId(),
      agent_id: this.user_id,
      assignedUserList: this.rows?.length > 0 ? this.rows : [],
    };
    this.listingService.assignedOtherAgent(payload).subscribe({
      next: response => {
        const { status } = response;
        if (status) {
          this.getUser(this.user_id);
          this.toastr.success('Account status updated successfully');
        }
      },
      error: _ => {
        this.toastr.error('Something went wrong');
      },
    });
  }

  OpenConfirmationAccountStatus(value) {
    let payload = {}; // Initialize an empty payload object

    if (value) {
      // Case when value is true: Reactivating an agent's account
      payload = {
        office_id: this.api.getOfficeId(), // Retrieve office ID from API service
        agent_id: this.user_id, // Assign current user ID
        assignedUserList: [], // Empty list of assigned users
        deactive: false, // Explicitly setting deactive to false (reactivation)
      };

      // Make API request to update agent status
      this.listingService.assignedOtherAgent(payload).subscribe({
        next: response => {
          const { status } = response;
          if (status) {
            this.toastr.success('Account status updated successfully'); // Show success message
            this.getUser(this.user_id); // Refresh user data after successful update
          }
        },
        error: _ => {
          this.toastr.error('Something went wrong'); // Show error message on failure
        },
      });
    } else {
      // Case when value is false: Deactivating an agent's account
      payload = {
        office_id: this.api.getOfficeId(), // Retrieve office ID
        agent_id: this.user_id, // Assign current user ID
        assignedUserList: [], // Empty assigned user list
      };

      // Make API request to deactivate agent status
      this.listingService.assignedOtherAgent(payload).subscribe({
        next: response => {
          const { status } = response;
          if (status) {
            this.toastr.success('Account status updated successfully'); // Show success message
            this.getUser(this.user_id); // Refresh user data after successful update
          }
        },
        error: _ => {
          this.toastr.error('Something went wrong'); // Show error message on failure
        },
      });
    }
  }

  // For Add Team Popup
  public isLoading = false;
  addTeam: FormGroup;
  users: any = [];
  public teamUrl: any;

  loadUsers() {
    this.addTeam.controls['members'].setValue([]);
    this.users = [];
    let office_id = this.addTeam.controls['office_id'].value || null;
    // If an office is selected, fetch its users (staff only)
    if (office_id) {
      // Fetch users by office and categorize them as active or deactivated
      this.api.get('/users/byoffice?office=' + office_id + '&show=staff').subscribe(res => {
        // Filter and map active staff
        let active_staff = res.data
          ?.filter(el => el?.deactive !== true)
          .map(i => {
            i = { ...i, full_name: i.first_name + ' ' + i.last_name };
            return i;
          });
        this.users = active_staff;
      });
    }
  }

  createTeam() {
    // Validate all form fields before submitting
    this.formHelper.validateAllFormFields(this.addTeam);

    // Proceed if the form is valid
    if (this.addTeam.valid) {
      this.isLoading = true; // Set loading state to true

      // Ensure team leader is included in the members list
      if (this.addTeam.value.leader && !this.addTeam.value.members.includes(this.addTeam.value.leader)) {
        this.addTeam.value.members.push(this.addTeam.value.leader);
      }

      let payload = this.addTeam.value;
      payload['user_id'] = this.api.getUserId();
      // API call to create a new team
      this.userService.saveTeams(payload).subscribe({
        next: _ => {
          this.isLoading = false; // Reset loading state
          this.toastr.success('Team created'); // Show success message
          this.addTeam.reset(); // Reset the form
          this.close(); // Close modal or form view
          this.getTeams(); // Refresh team list
          this.GetAssociatedTeam();
        },
        error: error => {
          this.toastr.error(error?.message);
        },
      });
    }
  }

  onTeamSelectFile(event) {
    const target = event.target as HTMLInputElement;

    if (target.files && target.files.length) {
      const file = target.files[0];

      // ✅ Step 1: Check for allowed formats
      const allowedTypes = ['image/jpg', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        this.toastr.error('Unsupported file format! Please upload valid image');
        target.value = ''; // Reset the file input
        return;
      }

      // ✅ Step 2: Read file if valid
      const fileReader: FileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        this.teamUrl = fileReader.result; // Store file URL
        this.addTeam.patchValue({
          photo: fileReader.result, // Update form field with uploaded file data
        });
      };
    }

    event.target.value = '';
  }

  delProfileImage(event) {
    event.stopPropagation();
    this.addTeam.patchValue({
      photo: null,
    });
    this.teamUrl = null;
  }
}
